export const DEFAULT_PUBLIC_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact Wisedoc support.';

export const PUBLIC_JD_ANALYSIS_ERROR_MESSAGE =
  'JD analysis could not be completed. Please retry the analysis or contact Wisedoc support.';

export const PUBLIC_CANDIDATE_REVIEW_ERROR_MESSAGE =
  'Candidate review could not be completed. Please retry the review or contact Wisedoc support.';

const UNSAFE_ERROR_PATTERNS = [
  /\bOpenAI\b/i,
  /\bAnthropic\b/i,
  /\bBedrock\b/i,
  /\bLangChain\b/i,
  /\bResponses API\b/i,
  /\bRuntimeError\b/i,
  /\bTraceback\b/i,
  /\b(TypeError|ReferenceError|SyntaxError|ValidationException|ConditionalCheckFailedException|ResourceNotFoundException|AccessDeniedException)\b/,
  /\b(AWS|Lambda|DynamoDB|SQS|EventBridge|Cognito)\b/,
  /\b(response_format|invalid_json_schema|invalid_request_error|text\.format\.schema)\b/i,
  /\b(OPENAI_API_KEY|Authorization header|PARSER_API_URL|LAMBDA_NAME|QUEUE_URL|TABLE_NAME)\b/i,
  /\b[A-Z0-9_]*(?:API|URL|NAME|QUEUE|TABLE|KEY|TOKEN|SECRET|LAMBDA)[A-Z0-9_]*\b/,
  /\n\s*at\s+\S+/,
  /\{\s*"error"\s*:/i,
];

const asText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const isUnsafeBackendErrorMessage = (value: unknown): boolean => {
  const text = asText(value);
  return Boolean(
    text && UNSAFE_ERROR_PATTERNS.some((pattern) => pattern.test(text)),
  );
};

export const toPublicErrorMessage = (
  value: unknown,
  fallback = DEFAULT_PUBLIC_ERROR_MESSAGE,
): string => {
  const text = asText(value);
  if (!text || isUnsafeBackendErrorMessage(text)) {
    return fallback;
  }
  return text;
};

export const sanitizeApiError = <T>(
  error: T,
  fallback = DEFAULT_PUBLIC_ERROR_MESSAGE,
): T => {
  const mutable = error as {
    message?: string;
    response?: {
      data?: {
        error?: unknown;
        message?: unknown;
      };
    };
  };

  if (typeof mutable.response?.data?.error === 'string') {
    mutable.response.data.error = toPublicErrorMessage(
      mutable.response.data.error,
      fallback,
    );
  }
  if (
    mutable.response?.data?.error &&
    typeof mutable.response.data.error === 'object'
  ) {
    const nestedError = mutable.response.data.error as { message?: unknown };
    if (typeof nestedError.message === 'string') {
      nestedError.message = toPublicErrorMessage(nestedError.message, fallback);
    }
  }
  if (typeof mutable.response?.data?.message === 'string') {
    mutable.response.data.message = toPublicErrorMessage(
      mutable.response.data.message,
      fallback,
    );
  }
  if (typeof mutable.message === 'string') {
    mutable.message = toPublicErrorMessage(mutable.message, fallback);
  }

  return error;
};
